import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Typography,
  Divider,
  Input,
  Button,
  Form,
  notification,
  Space,
} from "antd";
import { ref as dbRef, update } from "firebase/database";
import { rtdb } from "../../../../firebase.config";
import { UserContext } from "../../../../context/UserContext";

const { Title, Text } = Typography;

const formatPhone = (phone) => {
  if (!phone) return "";

  let trimmed = phone.trim();

  // If user entered only digits (10–15)
  if (/^\d{10,15}$/.test(trimmed)) {
    return `+1${trimmed}`;
  }

  // If already starts with +
  if (trimmed.startsWith("+")) {
    return trimmed;
  }

  return trimmed;
};

const isPhoneValid = (phone) => {
  const formatted = formatPhone(phone);
  const digitsOnly = formatted.replace(/\D/g, "");
  return digitsOnly.length >= 10 && digitsOnly.length <= 15;
};

export default function PhoneChangePage() {
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const [api, contextHolder] = notification.useNotification();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      api.error({
        message: "Not signed in",
        description: "Please sign in to change your phone number.",
      });
      navigate("/landlord/settings");
    }
  }, [user?.uid, api, navigate]);

  const handleSave = async () => {
    try {
      let raw = form.getFieldValue("displayPhone");
      const formatted = formatPhone(raw);

      if (!isPhoneValid(formatted)) {
        api.error({
          message: "Invalid phone number",
          description:
            "Enter a valid US or international phone number (10–15 digits)",
        });
        return;
      }

      setSaving(true);
      await update(dbRef(rtdb, `landlords/${user.uid}`), {
        displayPhone: raw.trim(),
      });

      setUser({
        ...user,
        displayPhone: formatted,
      });

      api.success({
        message: "Phone updated",
        description:
          "Your phone number was saved. Please verify it from the Settings page.",
      });

      navigate("/landlord/settings");
    } catch (err) {
      console.log(err);
      api.error({
        message: "Failed to update phone",
        description: err?.message || String(err),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 520 }}>
      {contextHolder}
      <Title level={3}>Change Phone Number</Title>
      <Text type="secondary">
        Update your phone number. You can verify it from the Settings page if
        verification is required.
      </Text>

      <Divider />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          displayPhone: "",
        }}
      >
        <Form.Item
          label="New phone number"
          name="displayPhone"
          rules={[
            { required: true, message: "Phone number is required" },
            {
              validator: (_, value) => {
                const formatted = formatPhone(value);
                return !value || isPhoneValid(formatted)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        "Enter a valid phone number (must be 10–15 digits)"
                      )
                    );
              },
            },
          ]}
        >
          <Input addonBefore="+1" maxLength={15} />
        </Form.Item>

        <Row justify="end">
          <Col>
            <Space>
              <Button onClick={() => navigate("/landlord/settings")}>
                Cancel
              </Button>
              <Button type="primary" loading={saving} onClick={handleSave}>
                Save
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </div>
  );
}
